import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Box,
  Container,
  Stack,
  Typography,
  Breadcrumbs,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  AppUserDetailsDto,
  VehicleDto,
  BatteryDto,
  AppUserGroupDto,
} from "../generated/api-client";
import { AppClient as ClientService } from "../services/client.service";
import { UserFieldsCard } from "../components/appUserDetail/UserFieldsCard";
import { GroupsSection } from "../components/appUserDetail/GroupsSection";

export default function AppUsersDetile() {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<AppUserDetailsDto | null>(
    null
  );

  const fetchUserDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const details = await ClientService.getInstance().appGET2(
        id,
        ClientService.version
      );
      setUserDetails(details);
    } catch (err: any) {
      setError(
        err.message || intl.formatMessage({ id: "failedToFetchUserDetails" })
      );
    } finally {
      setLoading(false);
    }
  }, [id, intl]);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id, fetchUserDetails]);

  const handleVehicleUpdate = useCallback(
    (groupIndex: number, vehicleIndex: number, updatedVehicle: VehicleDto) => {
      if (!userDetails) return;

      setUserDetails((prev) => {
        if (!prev || !prev.groups) return prev;

        const updatedGroups = [...prev.groups];
        if (updatedGroups[groupIndex] && updatedGroups[groupIndex].vehicles) {
          const updatedVehicles = [...updatedGroups[groupIndex].vehicles!];
          updatedVehicles[vehicleIndex] = updatedVehicle;
          updatedGroups[groupIndex] = new AppUserGroupDto({
            ...updatedGroups[groupIndex],
            vehicles: updatedVehicles,
          });
        }

        return new AppUserDetailsDto({
          user: prev.user,
          groups: updatedGroups,
        });
      });
    },
    [userDetails]
  );

  const handleBatteryUpdate = useCallback(
    (groupIndex: number, batteryIndex: number, updatedBattery: BatteryDto) => {
      if (!userDetails) return;

      setUserDetails((prev) => {
        if (!prev || !prev.groups) return prev;

        const updatedGroups = [...prev.groups];
        if (updatedGroups[groupIndex] && updatedGroups[groupIndex].batteries) {
          const updatedBatteries = [...updatedGroups[groupIndex].batteries!];
          updatedBatteries[batteryIndex] = updatedBattery;
          updatedGroups[groupIndex] = new AppUserGroupDto({
            ...updatedGroups[groupIndex],
            batteries: updatedBatteries,
          });
        }

        return new AppUserDetailsDto({
          user: prev.user,
          groups: updatedGroups,
        });
      });
    },
    [userDetails]
  );

  if (error || (!loading && !userDetails)) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          {error || intl.formatMessage({ id: "userNotFound" })}
        </Alert>
      </Container>
    );
  }

  const user = userDetails?.user;
  const groups = userDetails?.groups || [];

  const isLoading = loading;

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100%",
        }}
      >
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              {intl.formatMessage({ id: "dashboard" })}
            </Link>
            <Link
              to="/app-users"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {intl.formatMessage({ id: "appUsers" })}
            </Link>
            <Typography color="text.primary">
              {intl.formatMessage({ id: "detailsAppUser" })}
            </Typography>
          </Breadcrumbs>

          <Box>
            <Typography variant="h4">
              {intl.formatMessage({ id: "detailsAppUser" })}
            </Typography>
          </Box>
        </Box>

        <UserFieldsCard user={user} />

        <Divider />

        <Box>
          <Typography variant="h6" gutterBottom>
            {intl.formatMessage({ id: "groups" })} ({groups.length})
          </Typography>
          <GroupsSection
            groups={groups}
            onVehicleUpdate={handleVehicleUpdate}
            onBatteryUpdate={handleBatteryUpdate}
          />
        </Box>
      </Stack>
    </Container>
  );
}
